// AdminRooms.jsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AdminLayout } from './AdminDashboard';
import { LuImage, LuPlus, LuTrash2, LuPencil, LuBed, LuUsers, LuCheckCircle, LuXCircle } from 'react-icons/lu';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const baseUrl = axios.defaults.baseURL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
  return `${baseUrl}${url}`;
};

const empty = { 
  name:'', type:'deluxe', description:'', shortDesc:'', pricePerNight:'', 
  capacity:2, bedType:'', size:'', view:'', amenities:'', 
  isFeatured:false, isAvailable:true, 
  images: [] 
};

export default function AdminRooms() {
  const [showForm, setShowForm] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [form, setForm] = useState(empty);
  const qc = useQueryClient();

  const { data: rooms } = useQuery({
    queryKey: ['admin-rooms'],
    queryFn: () => axios.get('/api/rooms').then(r => r.data).catch(() => [])
  });

  const saveMutation = useMutation({
    mutationFn: (data) => {
      const payload = { 
        ...data, 
        amenities: typeof data.amenities === 'string' ? data.amenities.split(',').map(a => a.trim()).filter(Boolean) : data.amenities, 
        pricePerNight: Number(data.pricePerNight), 
        size: Number(data.size), 
        capacity: Number(data.capacity) 
      };
      return editRoom ? axios.put(`/api/rooms/${editRoom._id}`, payload) : axios.post('/api/rooms', payload);
    },
    onSuccess: () => { 
      toast.success('Room saved successfully!'); 
      qc.invalidateQueries(['admin-rooms']); 
      setShowForm(false); 
      setEditRoom(null); 
      setForm(empty); 
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/rooms/${id}`),
    onSuccess: () => { 
      toast.success('Room deleted'); 
      qc.invalidateQueries(['admin-rooms']); 
    }
  });

  const handleEdit = (r) => {
    setEditRoom(r);
    setForm({ 
      ...r, 
      amenities: r.amenities?.join(', ') || '',
      images: r.images || []
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading('Uploading room image...');
    try {
      const url = await uploadToCloudinary(file);
      
      const newImage = { url, alt: form.name || 'Room image', isPrimary: form.images.length === 0 };
      setForm(p => ({ ...p, images: [...p.images, newImage] }));
      toast.success('Image uploaded!', { id: toastId });
    } catch (err) {
      toast.error(err.message || 'Upload failed', { id: toastId });
    }
    e.target.value = '';
  };

  const removeImage = (index) => {
    setForm(p => ({
      ...p,
      images: p.images.filter((_, i) => i !== index)
    }));
  };

  const setPrimary = (index) => {
    setForm(p => ({
      ...p,
      images: p.images.map((img, i) => ({ ...img, isPrimary: i === index }))
    }));
  };

  const f = (field) => ({ value: form[field], onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

  return (
    <>
      <Helmet><title>Rooms – Villa Tesori Admin</title></Helmet>
      <AdminLayout title="Room Management">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', color: 'var(--forest)', marginBottom: 4 }}>Villa Accommodations</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Manage your luxury rooms and cottage details</p>
          </div>
          {!showForm && (
            <button 
              className="btn btn-primary" 
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px' }} 
              onClick={() => { setShowForm(true); setEditRoom(null); setForm(empty); }}
            >
              <LuPlus size={18} /> Add New Room
            </button>
          )}
        </div>

        {showForm && (
          <div style={{ background: 'var(--white)', border: '1px solid #e8e8e8', borderRadius: 4, padding: 32, marginBottom: 40, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem', color: 'var(--forest)' }}>
                {editRoom ? 'Edit Accommodation' : 'New Accommodation'}
              </h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.88rem' }}>Cancel</button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div className="form-group"><label className="form-label">Room Name *</label><input className="form-control" {...f('name')} required placeholder="e.g. Vellarimala Suite" /></div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-control" {...f('type')}>
                  {['deluxe','premium','suite','cottage'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 32 }}>
              <label className="form-label">Room Gallery</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16, marginTop: 12 }}>
                {form.images.map((img, idx) => (
                  <div key={idx} style={{ position: 'relative', height: 100, border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden', background: '#fafafa' }}>
                    <img src={getImageUrl(img.url)} alt="Room preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 4 }}>
                      <button 
                        onClick={() => removeImage(idx)} 
                        type="button" 
                        style={{ padding: 4, background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 2, cursor: 'pointer', color: '#c00' }}
                      >
                        <LuTrash2 size={12} />
                      </button>
                    </div>
                    {img.isPrimary && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'var(--gold)', color: 'var(--forest)', fontSize: '0.65rem', textAlign: 'center', padding: '2px 0', fontWeight: 600 }}>PRIMARY</div>}
                    {!img.isPrimary && (
                      <button 
                        onClick={() => setPrimary(idx)}
                        type="button"
                        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', color: '#fff', border: 'none', fontSize: '0.7rem', opacity: 0, transition: 'opacity 0.2s', cursor: 'pointer' }}
                        onMouseEnter={e => e.target.style.opacity = 1}
                        onMouseLeave={e => e.target.style.opacity = 0}
                      >
                        Set Primary
                      </button>
                    )}
                  </div>
                ))}
                <div 
                  onClick={() => document.getElementById('room-image-upload').click()}
                  style={{ height: 100, border: '2px dashed #ddd', borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fafafa', color: '#888' }}
                >
                  <LuPlus size={24} />
                  <span style={{ fontSize: '0.7rem', marginTop: 4 }}>Add Image</span>
                </div>
              </div>
              <input id="room-image-upload" type="file" hidden accept="image/*" onChange={handleFileUpload} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div className="form-group"><label className="form-label">Price Per Night (₹) *</label><input className="form-control" type="number" {...f('pricePerNight')} required /></div>
              <div className="form-group"><label className="form-label">Capacity (Adults)</label><input className="form-control" type="number" {...f('capacity')} /></div>
              <div className="form-group"><label className="form-label">Bed Type</label><input className="form-control" {...f('bedType')} placeholder="King Bed, Queen Bed..." /></div>
              <div className="form-group"><label className="form-label">Room Size (sq ft)</label><input className="form-control" type="number" {...f('size')} /></div>
              <div className="form-group"><label className="form-label">View Type</label><input className="form-control" {...f('view')} placeholder="Mountain, Garden, Valley..." /></div>
              <div className="form-group"><label className="form-label">Short Tagline</label><input className="form-control" {...f('shortDesc')} maxLength={60} /></div>
            </div>

            <div className="form-group"><label className="form-label">Full Room Description</label><textarea className="form-control" {...f('description')} rows={4} /></div>
            <div className="form-group"><label className="form-label">Amenities (comma-separated)</label><input className="form-control" {...f('amenities')} placeholder="AC, WiFi, Balcony, Mini Bar..." /></div>
            
            <div style={{ display: 'flex', gap: 32, marginBottom: 32, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', color: 'var(--forest)' }}>
                <input type="checkbox" checked={form.isFeatured} onChange={e => setForm(p => ({ ...p, isFeatured: e.target.checked }))} style={{ width: 18, height: 18, accentColor: 'var(--forest)' }} />
                Featured Highlight
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', color: 'var(--forest)' }}>
                <input type="checkbox" checked={form.isAvailable} onChange={e => setForm(p => ({ ...p, isAvailable: e.target.checked }))} style={{ width: 18, height: 18, accentColor: 'var(--forest)' }} />
                Available for Booking
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                className="btn btn-primary" 
                style={{ padding: '12px 32px' }} 
                onClick={() => saveMutation.mutate(form)}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Accommodation'}
              </button>
              <button className="btn btn-outline" style={{ padding: '12px 24px' }} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Room List Grid */}
        <div style={{ background: 'var(--white)', borderRadius: 4, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          {(rooms || []).length === 0 && (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>No accommodations listed. Add your first room.</div>
          )}
          {(rooms || []).map(room => (
            <div key={room._id} style={{ padding: '20px 24px', borderBottom: '1px solid #f5f5f5', display: 'flex', gap: 24, alignItems: 'center', transition: 'background 0.2s' }}>
              <div style={{ width: 100, height: 70, borderRadius: 4, background: '#f5f5f5', overflow: 'hidden', flexShrink: 0 }}>
                {room.images?.find(i => i.isPrimary)?.url ? (
                  <img src={getImageUrl(room.images.find(i => i.isPrimary).url)} alt={room.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                    <LuImage size={24} />
                  </div>
                )}
              </div>
              
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <h4 style={{ fontWeight: 600, color: 'var(--forest)', fontSize: '1rem' }}>{room.name}</h4>
                  <span style={{ padding: '2px 8px', background: '#e8f0f8', color: '#1a4a8a', borderRadius: 4, fontSize: '0.68rem', textTransform: 'uppercase' }}>{room.type}</span>
                  {room.isFeatured && <span style={{ padding: '2px 8px', background: '#fdf6e3', color: '#856404', borderRadius: 4, fontSize: '0.68rem', fontWeight: 600 }}>FEATURED</span>}
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><LuBed size={12} /> {room.bedType || 'N/A'}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><LuUsers size={12} /> {room.capacity} Guests</span>
                  <span>₹{room.pricePerNight?.toLocaleString()} / night</span>
                  <span>{room.view} view</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 12 }}>
                  {room.isAvailable ? <LuCheckCircle size={14} color="#2d5a2d" /> : <LuXCircle size={14} color="#c00" />}
                  <span style={{ fontSize: '0.72rem', color: room.isAvailable ? '#2d5a2d' : '#c00', fontWeight: 600 }}>
                    {room.isAvailable ? 'AVAILABLE' : 'OFFLINE'}
                  </span>
                </div>
                <button 
                  onClick={() => handleEdit(room)} 
                  style={{ padding: '8px', borderRadius: 4, border: '1px solid #eee', background: '#fff', cursor: 'pointer', color: 'var(--forest)' }}
                  title="Edit Room"
                >
                  <LuPencil size={16} />
                </button>
                <button 
                  onClick={() => { if (window.confirm(`Delete ${room.name}?`)) deleteMutation.mutate(room._id); }} 
                  style={{ padding: '8px', borderRadius: 4, border: '1px solid #ffebeb', background: '#fff', cursor: 'pointer', color: '#c00' }}
                  title="Delete Room"
                >
                  <LuTrash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </AdminLayout>
    </>
  );
}
